import { useCallback, useEffect, useState } from 'react';
import { useGetOne, useRecordContext } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';

import { useLoadGoogleMaps } from '../hooks/useLoadGoogleMaps';
import {
  BILL_ROUND_TRIP,
  distanceBeyondZone,
  estimateDrivingKm,
  LatLng,
  RATES,
  testZone,
  transportCharge,
  ZoneRegion,
} from '../pricing/zone';
import { coordsOf, geocode } from '../pricing/pickupLocation';
import { usePickupLocation } from '../pricing/usePickupLocation';
import { Contract } from '../types';

const money = (n: number) =>
  n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });

type Computed = {
  inZone: boolean;
  municipality?: string;
  km?: number;
  amount?: number;
  crossing?: LatLng;
  crossingMunicipality?: string;
  measured?: boolean;
  coords: LatLng;
};

/**
 * The out-of-zone transport charge, on the contract.
 *
 * Shows where the transfer starts, whether that is inside Zone 1, and what the
 * kilometres beyond it come to — then writes the line on request.
 *
 * Deliberately NOT automatic on every render. The place of death changes during
 * arrangement, often after the family has been quoted a number, and silently
 * repricing a contract someone has already read is the failure this whole
 * feature has to avoid. Calculating is a button, and it says what it will change
 * before it changes it.
 */
export const ContractTransportPanel = () => {
  const contract = useRecordContext<Contract>();
  const { setValue } = useFormContext();
  const lines = useWatch({ name: 'lines' }) as any[] | undefined;

  const { isLoaded } = useLoadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  const { data: funeral } = useGetOne(
    'funerals',
    { id: contract?.funeral_id as any },
    { enabled: contract?.funeral_id != null },
  );

  const [computed, setComputed] = useState<Computed | null>(null);
  /**
   * The zone answer alone, shown without anyone asking for it.
   *
   * Point-in-polygon is local and instant — no network, no cost — so there is no
   * reason to make someone press a button to find out that a case is covered.
   * Only the geocode and the routing call are withheld: those cost money and,
   * more importantly, produce a price, and a price must never appear on a
   * contract without someone choosing to put it there.
   */
  const [zoneOnly, setZoneOnly] = useState<
    { inZone: true; municipality: string } | { inZone: false } | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const region: ZoneRegion =
    (funeral?.region ?? '').toLowerCase() === 'ontario' ? 'ontario' : 'quebec';
  const pickup = usePickupLocation(funeral ?? null);
  const rate = RATES[region];

  // The line already on the contract, if any — matched by item id, since the
  // item names have changed once already.
  const existing = (lines ?? []).find(
    (l) => String(l?.item) === rate.quickbooksItemId,
  );

  const calculate = useCallback(async () => {
    if (!pickup) return;
    setBusy(true);
    setError(null);
    setComputed(null);
    try {
      const coords = coordsOf(pickup.address) ?? (await geocode(pickup.address));
      const zone = testZone(coords, region);

      if (zone.inZone) {
        setComputed({ inZone: true, municipality: zone.municipality, coords });
        return;
      }

      try {
        const beyond = await distanceBeyondZone(coords, region);
        const charge = transportCharge(beyond.km, region);
        setComputed({
          inZone: false,
          km: charge.billableKm,
          amount: charge.amount,
          crossing: beyond.crossing,
          crossingMunicipality: beyond.crossingMunicipality,
          measured: true,
          coords,
        });
      } catch {
        // No route: fall back to the straight line, clearly marked. A rough
        // number the arranger knows is rough beats refusing to price at all.
        const charge = transportCharge(estimateDrivingKm(zone.straightLineKm), region);
        setComputed({
          inZone: false,
          km: charge.billableKm,
          amount: charge.amount,
          crossingMunicipality: zone.borderMunicipality,
          measured: false,
          coords,
        });
      }
    } catch (e: any) {
      setError(e?.message ?? 'Could not work out where this pickup is');
    } finally {
      setBusy(false);
    }
  }, [pickup, region]);

  // Clear a stale result when the pickup address changes underneath it, and
  // re-run the free part immediately.
  useEffect(() => {
    setComputed(null);
    setError(null);

    const coords = pickup ? coordsOf(pickup.address) : null;
    if (!coords) {
      // No stored coordinates. Finding them means a geocode, which is a network
      // call — so it waits for the button rather than firing on every contract
      // that gets opened.
      setZoneOnly(null);
      return;
    }
    const zone = testZone(coords, region);
    setZoneOnly(
      zone.inZone ? { inZone: true, municipality: zone.municipality } : { inZone: false },
    );
  }, [pickup?.label, pickup, region]);

  const applyLine = () => {
    if (!computed || computed.inZone || computed.km == null) return;

    const km = Math.round(computed.km);
    const line = {
      item: rate.quickbooksItemId,
      name: `${region === 'ontario' ? 'ON' : 'QC'} | Outside Zone Transportation`,
      // Says what it measured from, because a family who checks the distance
      // themselves will route from the town and get a different number.
      description: `${km} km${BILL_ROUND_TRIP ? ' round trip' : ''} from the zone edge at ${
        computed.crossingMunicipality ?? 'the boundary'
      }${computed.measured ? '' : ' (estimated)'}`,
      price: rate.perKm,
      quantity: km,
      total: Number((km * rate.perKm).toFixed(2)),
      // Mirrors what a hand-entered transport line carries. `tax` is the
      // QuickBooks tax code and is mandatory — without it the estimate sync
      // refuses the whole contract, not just this line.
      tax: rate.quickbooksTaxCode,
      type: '_',
    };

    const next = [...(lines ?? [])];
    const at = next.findIndex((l) => String(l?.item) === rate.quickbooksItemId);
    if (at >= 0) next[at] = { ...next[at], ...line };
    else next.push(line);
    setValue('lines', next, { shouldDirty: true });

    // What produced the charge, kept with the contract. Without this nobody can
    // answer why a family was billed this, or whether the rate covers cost.
    setValue(
      'transport_pricing',
      {
        zone_version: 'v1',
        region,
        pickup: computed.coords,
        pickup_label: pickup?.label,
        pickup_overridden: pickup?.overridden ?? false,
        in_zone: false,
        crossing: computed.crossing ?? null,
        crossing_municipality: computed.crossingMunicipality ?? null,
        billable_km: km,
        measured_km: Number(computed.km.toFixed(2)),
        rate: rate.perKm,
        quickbooks_item_id: rate.quickbooksItemId,
        distance_method: computed.measured ? 'routed' : 'straight_line_estimate',
        calculated_at: new Date().toISOString(),
      },
      { shouldDirty: true },
    );
  };

  if (!contract?.funeral_id) return null;

  return (
    <Box sx={{ my: 2, p: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Transport
      </Typography>

      {!pickup ? (
        <Alert severity="info">
          No address on the place of death, so the zone can't be checked. Add one on the
          funeral, or set a pickup address if the body is collected elsewhere.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {pickup.overridden ? 'Pickup (overridden)' : 'Place of death'}: {pickup.label}
          </Typography>
          {pickup.overridden && (
            <Typography variant="caption" color="warning.main">
              Collected somewhere other than the place of death — kilometres are measured
              from here.
            </Typography>
          )}

          {/* The zone answer, whether or not anyone asked — so it is obvious at
              a glance that this is working, and obvious when a case is covered
              and needs nothing further. */}
          {zoneOnly?.inZone && !computed && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Chip label="Inside Zone 1" color="success" size="small" />
              <Typography variant="body2" color="text.secondary">
                {zoneOnly.municipality} — transport is included, nothing to add.
              </Typography>
            </Stack>
          )}
          {zoneOnly && !zoneOnly.inZone && !computed && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Chip label="Outside Zone 1" color="warning" size="small" />
              <Typography variant="body2" color="text.secondary">
                Needs a transport charge — calculate to price it.
              </Typography>
            </Stack>
          )}
          {!zoneOnly && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              No coordinates on file for this address, so the zone hasn't been checked
              yet.
            </Typography>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={() => void calculate()} disabled={busy || !isLoaded}>
              {busy ? 'Calculating…' : existing ? 'Recalculate transport' : 'Calculate transport'}
            </Button>
            {existing && (
              <Typography variant="caption" color="text.secondary">
                Currently on the contract: {existing.quantity} km ={' '}
                {money(Number(existing.total ?? 0))}
              </Typography>
            )}
          </Stack>

          {error && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {error} — price this one by hand rather than guessing.
            </Alert>
          )}

          {computed?.inZone && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <Chip label="Inside Zone 1" color="success" size="small" />
              <Typography variant="body2">
                {computed.municipality} — transport is included, nothing to add.
              </Typography>
            </Stack>
          )}

          {computed && !computed.inZone && computed.km != null && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label="Outside Zone 1" color="warning" size="small" />
                <Typography variant="body2">
                  {computed.km.toFixed(1)} km × {money(rate.perKm)} ={' '}
                  <strong>{money(computed.amount ?? 0)}</strong>
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Measured from the zone edge at {computed.crossingMunicipality}
                {computed.measured ? '' : ', estimated — no driving route available'}.
              </Typography>

              {/* Says what will change before it changes it. */}
              {existing && Number(existing.total ?? 0) !== Number((computed.amount ?? 0).toFixed(2)) && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This replaces the current line: {money(Number(existing.total ?? 0))} →{' '}
                  {money(computed.amount ?? 0)}.
                </Alert>
              )}

              <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={applyLine}>
                {existing ? 'Update the line' : 'Add to contract'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ContractTransportPanel;
